import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { secureStorage } from '../utils/secureStorage'

interface User {
  id: string
  username: string
  email: string
  role?: string // Primary role
  roles?: string[] // All roles as array
  companyId?: string
  branchId?: string
  companyCode?: string
  companyName?: string
  branchCode?: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  mobileNumber?: string
  /** Warehouse bound to this user/employee/rep — sales locked to it when set */
  assignedStoreId?: string
  assignedStoreName?: string
  lockStoreToAssigned?: boolean
  /** Sales rep linked to this login user (non-admin) */
  salesRepId?: number
  salesRepName?: string
  lockSalesRepToAssigned?: boolean
  restrictByBranchAndWarehouse?: boolean
  viewAllBranchesAndWarehouses?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (user: User, token: string) => void
  /** Replace JWT only (same user) — e.g. session refresh. */
  setToken: (token: string) => void
  /** Merge fields into current user (e.g. companyId from GET /auth/me). */
  mergeUser: (partial: Partial<User>) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      mergeUser: (partial) =>
        set((state) => {
          if (!state.user) return state
          const next = { ...state.user }
          for (const [key, value] of Object.entries(partial)) {
            if (value !== undefined) {
              ;(next as Record<string, unknown>)[key] = value
            }
          }
          return { user: next as User }
        }),
      setToken: (token) => {
        if (token) {
          secureStorage.setItem('token', token)
        }
        set((state) => ({
          token,
          isAuthenticated: !!(token && state.user),
        }))
      },
      login: (user, token) => {
        // Store token in localStorage for apiClient
        if (token) {
          secureStorage.setItem('token', token)
        }
        set({ user, token, isAuthenticated: true })
        // Trigger cross-tab sync by updating a sync key
        // Use a unique timestamp to ensure the event fires
        const syncData = JSON.stringify({ 
          action: 'login', 
          timestamp: Date.now(),
          userId: user.id
        })
        secureStorage.setItem('auth-sync', syncData)
        // Remove it after a short delay to allow future changes
        setTimeout(() => {
          try {
            secureStorage.removeItem('auth-sync')
          } catch (e) {
            // Ignore errors
          }
        }, 100)
      },
      logout: () => {
        secureStorage.removeItem('token')
        secureStorage.removeItem('user')
        set({ user: null, token: null, isAuthenticated: false })
        // Trigger cross-tab sync by updating a sync key
        // Use a unique timestamp to ensure the event fires
        const syncData = JSON.stringify({ 
          action: 'logout', 
          timestamp: Date.now() 
        })
        secureStorage.setItem('auth-sync', syncData)
        // Remove it after a short delay to allow future changes
        setTimeout(() => {
          try {
            secureStorage.removeItem('auth-sync')
          } catch (e) {
            // Ignore errors
          }
        }, 100)
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => secureStorage),
      // On rehydration, sync isAuthenticated with token and user
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Check if we have both token and user
          const hasToken = !!state.token
          const hasUser = !!state.user
          state.isAuthenticated = hasToken && hasUser
          
          // Also sync localStorage token
          if (hasToken && state.token) {
            secureStorage.setItem('token', state.token)
          } else {
            secureStorage.removeItem('token')
          }
        }
      },
    }
  )
)

